export type FilterDisplayMode = "compact" | "detailed"
export type WorkOrderDateRangeFilter = {
  start: Date | null
  end: Date | null
}
export type WorkOrderEmployeeFilterOption = {
  id: string
  label: string
}

export type WorkOrderOperationalState =
  | "NO_APPOINTMENT"
  | "ANNOUNCEMENT_PLAN_MISSING"
  | "ANNOUNCEMENT_RUNNING"
  | "LETTER_DUE"
  | "SECURELY_ANNOUNCED"
  | "APPOINTMENT_CONFIRMED"
  | "WORK_ORDER_COMPLETED"
export type WorkOrderActionRequiredState = "" | "required" | "not_yet_required" | "not_required"

export type WorkOrderFilterValue = {
  searchQuery: string
  orderTypeIds: string[]
  appointmentDateRange: WorkOrderDateRangeFilter
  employeeIds: string[]
  dueDateRange: WorkOrderDateRangeFilter
  workOrderStates: WorkOrderOperationalState[]
  actionRequiredState: WorkOrderActionRequiredState
  archiveScope: ArchiveScope
}

export const ARCHIVE_SCOPE_OPTIONS: { name: string; value: ArchiveScope }[] = [
  { name: "Aktiv", value: "ACTIVE" },
  { name: "Archiviert", value: "ARCHIVED" },
  { name: "Alle", value: "ALL" },
]

export const WORK_ORDER_OPERATIONAL_STATE_OPTIONS: {
  name: string
  value: WorkOrderOperationalState
}[] = [
  { name: "Ohne Termin", value: "NO_APPOINTMENT" },
  { name: "Ohne Ankündigungsplan", value: "ANNOUNCEMENT_PLAN_MISSING" },
  { name: "Ankündigung läuft", value: "ANNOUNCEMENT_RUNNING" },
  { name: "Terminbrief fällig", value: "LETTER_DUE" },
  { name: "Auftrag gesichert angekündigt", value: "SECURELY_ANNOUNCED" },
  { name: "Termin bestätigt", value: "APPOINTMENT_CONFIRMED" },
  { name: "Auftrag abgeschlossen", value: "WORK_ORDER_COMPLETED" },
]

export const WORK_ORDER_ACTION_REQUIRED_STATE_OPTIONS: {
  name: string
  value: WorkOrderActionRequiredState
}[] = [
  { name: "Alle Aktionszustände", value: "" },
  { name: "Erforderlich", value: "required" },
  { name: "Noch nicht erforderlich", value: "not_yet_required" },
  { name: "Nicht erforderlich", value: "not_required" },
]

export const createEmptyDateRange = (): WorkOrderDateRangeFilter => ({
  start: null,
  end: null,
})

export const createDefaultWorkOrderFilters = (): WorkOrderFilterValue => ({
  searchQuery: "",
  orderTypeIds: [],
  appointmentDateRange: createEmptyDateRange(),
  employeeIds: [],
  dueDateRange: createEmptyDateRange(),
  workOrderStates: [],
  actionRequiredState: "",
  archiveScope: "ACTIVE",
})

const coerceDate = (value?: Date | string | number | null): Date | null => {
  if (!value) return null

  const parsed = value instanceof Date ? value : new Date(value)

  return Number.isNaN(parsed.getTime()) ? null : parsed
}

export const normalizeDateRangeFilter = (
  value?: Partial<WorkOrderDateRangeFilter> | null,
): WorkOrderDateRangeFilter => ({
  start: coerceDate(value?.start ?? null),
  end: coerceDate(value?.end ?? null),
})

export const dateRangeFiltersEqual = (
  left?: Partial<WorkOrderDateRangeFilter> | null,
  right?: Partial<WorkOrderDateRangeFilter> | null,
) => {
  const a = normalizeDateRangeFilter(left)
  const b = normalizeDateRangeFilter(right)

  return (
    (a.start?.getTime() ?? null) === (b.start?.getTime() ?? null) &&
    (a.end?.getTime() ?? null) === (b.end?.getTime() ?? null)
  )
}

export const isDateRangeFilterEmpty = (value?: Partial<WorkOrderDateRangeFilter> | null) => {
  const normalized = normalizeDateRangeFilter(value)

  return normalized.start === null && normalized.end === null
}

export const formatDateParam = (value?: Date | string | null) => {
  const parsed = coerceDate(value ?? null)

  if (!parsed) return undefined

  const year = parsed.getFullYear()
  const month = `${parsed.getMonth() + 1}`.padStart(2, "0")
  const day = `${parsed.getDate()}`.padStart(2, "0")

  return `${year}-${month}-${day}`
}

const normalizeStringList = <T extends string>(
  values?: Array<T | string | null | undefined> | null,
): T[] =>
  (Array.isArray(values) ? values : [])
    .map((entry) => `${entry ?? ""}`.trim())
    .filter(Boolean) as T[]

const validStateValues = new Set(WORK_ORDER_OPERATIONAL_STATE_OPTIONS.map((entry) => entry.value))
const validActionRequiredStateValues = new Set(
  WORK_ORDER_ACTION_REQUIRED_STATE_OPTIONS.map((entry) => entry.value),
)

const normalizeActionRequiredState = (value?: string | null): WorkOrderActionRequiredState => {
  const normalized = `${value ?? ""}`.trim().toLowerCase()

  return validActionRequiredStateValues.has(normalized as WorkOrderActionRequiredState)
    ? (normalized as WorkOrderActionRequiredState)
    : ""
}

const normalizeArchiveScope = (value?: string | null): ArchiveScope =>
  value === "ARCHIVED" || value === "ALL" ? value : "ACTIVE"

export const normalizeWorkOrderFilterValue = (
  value?: Partial<WorkOrderFilterValue> | null,
): WorkOrderFilterValue => ({
  searchQuery: value?.searchQuery?.trim?.() ?? "",
  orderTypeIds: normalizeStringList(value?.orderTypeIds),
  appointmentDateRange: normalizeDateRangeFilter(value?.appointmentDateRange),
  employeeIds: normalizeStringList(value?.employeeIds),
  dueDateRange: normalizeDateRangeFilter(value?.dueDateRange),
  workOrderStates: normalizeStringList<WorkOrderOperationalState>(value?.workOrderStates).filter(
    (entry) => validStateValues.has(entry),
  ),
  actionRequiredState: normalizeActionRequiredState(value?.actionRequiredState),
  archiveScope: normalizeArchiveScope(value?.archiveScope),
})

const normalizeRefreshKeyList = (values: string[]): string[] =>
  Array.from(new Set(values.map((value) => value.trim()).filter(Boolean))).sort()

export const buildWorkOrderFilterRefreshKey = (
  value?: Partial<WorkOrderFilterValue> | null,
): string => {
  const normalized = normalizeWorkOrderFilterValue(value)

  return JSON.stringify({
    searchQuery: normalized.searchQuery,
    orderTypeIds: normalizeRefreshKeyList(normalized.orderTypeIds),
    appointmentDateFrom: formatDateParam(normalized.appointmentDateRange.start) ?? null,
    appointmentDateTo: formatDateParam(normalized.appointmentDateRange.end) ?? null,
    employeeIds: normalizeRefreshKeyList(normalized.employeeIds),
    dueDateFrom: formatDateParam(normalized.dueDateRange.start) ?? null,
    dueDateTo: formatDateParam(normalized.dueDateRange.end) ?? null,
    workOrderStates: normalizeRefreshKeyList(normalized.workOrderStates),
    actionRequiredState: normalized.actionRequiredState,
    archiveScope: normalized.archiveScope,
  })
}
